
import React, { useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import StudentTable from "@/components/students/StudentTable";
import { StudentMobileList } from "@/components/students/StudentMobileList";
import { Button } from "@/components/ui/button";
import { subDays } from "date-fns";
import { useCreatorStudents } from "@/hooks/useCreatorStudents";
import { StudentFilters } from "@/types/student";
import { devLog } from "@/utils/devLog";

interface StudentListSectionProps {
  creatorId: string;
  onViewStudent: (student: any) => void;
  filters: StudentFilters;
  setFilters: React.Dispatch<React.SetStateAction<StudentFilters>>;
}

export const StudentListSection: React.FC<StudentListSectionProps> = ({
  creatorId,
  onViewStudent,
  filters,
  setFilters,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [paginatedStudents, setPaginatedStudents] = useState<any[]>([]);
  const [totalStudents, setTotalStudents] = useState(0);

  // Fetch students data
  const { data: studentsData, isLoading } = useCreatorStudents(creatorId);

  // Apply filters and pagination
  useEffect(() => {
    if (studentsData) {
      devLog(`[StudentListSection] Processing ${studentsData.length} students with filters`);
      
      // Apply filters (simplified for example)
      let filteredStudents = studentsData;
      
      // Apply date filter
      if (filters.dateRange) {
        const fromDate = filters.dateRange.from;
        const toDate = filters.dateRange.to;
        
        filteredStudents = filteredStudents.filter(student => {
          const enrolledDate = new Date(student.enrolled_at);
          return enrolledDate >= fromDate && enrolledDate <= toDate;
        });
      }
      
      // Apply course filter
      if (filters.courseIds && filters.courseIds.length > 0) {
        filteredStudents = filteredStudents.filter(student => 
          filters.courseIds?.includes(student.course_id)
        );
      }
      
      // Apply search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredStudents = filteredStudents.filter(student =>
          student.student_name.toLowerCase().includes(searchTerm) ||
          student.student_email.toLowerCase().includes(searchTerm) ||
          student.course_title.toLowerCase().includes(searchTerm)
        );
      }
      
      // Apply progress filter
      if (filters.progress && filters.progress.min !== undefined && filters.progress.max !== undefined) {
        filteredStudents = filteredStudents.filter(student =>
          student.progress_percent >= filters.progress.min && 
          student.progress_percent <= filters.progress.max
        );
      }
      
      // Apply last active filter
      if (filters.lastActive) {
        const cutoffDate = subDays(new Date(), filters.lastActive);
        filteredStudents = filteredStudents.filter(student => {
          if (!student.last_active) return false;
          const lastActiveDate = new Date(student.last_active);
          return lastActiveDate >= cutoffDate;
        });
      }
      
      // Update total count
      setTotalStudents(filteredStudents.length);
      
      // Apply pagination
      const start = (currentPage - 1) * pageSize;
      const end = start + pageSize;
      const paginatedResults = filteredStudents.slice(start, end);
      
      devLog(`[StudentListSection] After filtering: ${filteredStudents.length} students, page ${currentPage}: ${paginatedResults.length} students`);
      setPaginatedStudents(paginatedResults);
    }
  }, [studentsData, filters, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  const openStudentDetail = (student: any) => {
    onViewStudent(student);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold flex items-center gap-2">
        Enrolled Students
      </h2>
      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : paginatedStudents && paginatedStudents.length > 0 ? (
        <>
          {/* Desktop view */}
          <div className="hidden md:block">
            <StudentTable
              students={paginatedStudents}
              isLoading={isLoading}
              openStudentDetail={openStudentDetail}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              totalStudents={totalStudents}
            />
          </div>
          {/* Mobile view */}
          <div className="md:hidden">
            <StudentMobileList
              students={paginatedStudents}
              isLoading={isLoading}
              openStudentDetail={openStudentDetail}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              totalStudents={totalStudents}
            />
          </div>
        </>
      ) : (
        <div className="text-center py-12 border rounded-lg">
          <h3 className="text-lg font-medium">No students found</h3>
          <p className="text-muted-foreground mt-2">
            Try adjusting your filters or search criteria.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() =>
              setFilters((prev: any) => ({
                ...prev,
                dateRange: {
                  from: subDays(new Date(), 30),
                  to: new Date(),
                },
                courseIds: undefined,
                progress: undefined,
                search: undefined,
              }))
            }
          >
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
};
